import React, { useRef } from "react";
import { Link } from "react-router-dom";
import { useLanguage } from "../../contexts/LanguageContext";
import Seo from "../../components/seo/Seo";
import { useCursor, useInkBody, useReveal, useV2Fonts } from "../hooks/useV2Effects";
import { pickLang } from "../copy";
import { route } from "../paths";
import "../v2.css";

interface Props {
  title: string;
  description?: string;
  /** Serif statement on the ink panel. */
  statement: React.ReactNode;
  /** Small mono line under the statement. */
  note?: string;
  img?: string;
  children: React.ReactNode;
}

/**
 * Split auth layout: ink panel with the brand statement on the left, a bone
 * paper card with the form on the right. Single column on mobile.
 */
const AuthShell: React.FC<Props> = ({ title, description, statement, note, img = "/illustrations/landing/composer.webp", children }) => {
  const rootRef = useRef<HTMLDivElement>(null);
  const ringRef = useRef<HTMLDivElement>(null);
  const { currentLanguage, changeLanguage } = useLanguage();
  const lang = pickLang(currentLanguage);
  const other = lang === "fr" ? "en" : "fr";

  useV2Fonts();
  useInkBody();
  useReveal(rootRef);
  useCursor(ringRef);

  return (
    <div ref={rootRef} className="ltb-v2 min-h-screen">
      <Seo title={`${title} | Lave Tes Baskets`} description={description ?? title} canonicalPath={route(lang)} noindex />
      <div className="grid min-h-screen lg:grid-cols-12">
        {/* ink panel */}
        <aside className="relative isolate flex flex-col overflow-hidden bg-ink px-5 pb-8 pt-6 text-bone sm:px-8 lg:col-span-5 lg:min-h-screen lg:px-12 lg:py-10">
          <div className="v2-glow pointer-events-none absolute -top-40 right-[-20%] -z-10 h-[60vh] w-[60vh] rounded-full bg-tangerine/[0.12] blur-[140px]" />
          <div className="flex items-center justify-between">
            <Link to={route(lang)} aria-label="Lave Tes Baskets">
              <img src="/lavetesbasket_logow.png" alt="Lave Tes Baskets" className="h-10 w-auto object-contain" />
            </Link>
            <button
              onClick={() => changeLanguage(other)}
              className="font-mono text-[11px] uppercase tracking-[0.18em] text-bone/70 hover:text-bone"
            >
              {`${lang} `}<span className="opacity-40">/</span>{` ${other}`}
            </button>
          </div>

          <div className="flex flex-1 flex-col justify-center py-10 lg:py-12">
            <div className="v2-reveal flex items-center gap-3 font-mono text-[10px] uppercase tracking-[0.22em] text-bone/60">
              <span className="h-2 w-2 rounded-full bg-teal-light" />
              {lang === "fr" ? "Atelier · Île-de-France" : "Atelier · Île-de-France"}
            </div>
            <p className="v2-reveal mt-4 font-display text-[clamp(2rem,4vw,3.6rem)] leading-[0.98] tracking-[-0.02em]" data-delay="1">
              {statement}
            </p>
            {note && (
              <p className="v2-reveal mt-4 max-w-sm text-sm text-bone/60" data-delay="2">
                {note}
              </p>
            )}

            {/* login.png / signup.png / verify.png in public/illustrations/landing — falls back to the configurator art. */}
            <img
              src={img}
              alt=""
              loading="lazy"
              width={900}
              height={600}
              className="v2-reveal mt-10 hidden max-h-[320px] w-auto max-w-[360px] object-contain object-left lg:block"
              data-delay="2"
              onError={(e) => {
                const el = e.currentTarget;
                if (!el.src.endsWith("/composer.webp")) el.src = "/illustrations/landing/composer.webp";
              }}
            />
          </div>

          <div className="hidden items-center gap-6 font-mono text-[10px] uppercase tracking-[0.2em] text-bone/40 lg:flex">
            <Link to={route(lang, "faq")} className="v2-link hover:text-bone">FAQ</Link>
            <Link to={`/${lang}/terms`} className="v2-link hover:text-bone">{lang === "fr" ? "CGU" : "Terms"}</Link>
            <Link to={`/${lang}/privacy`} className="v2-link hover:text-bone">{lang === "fr" ? "Confidentialité" : "Privacy"}</Link>
          </div>
        </aside>

        {/* paper panel */}
        <main className="flex items-start justify-center bg-bone px-5 py-10 text-ink sm:px-8 sm:py-14 lg:col-span-7 lg:items-center lg:px-16">
          <div className="w-full max-w-md">{children}</div>
        </main>
      </div>
      <div className="v2-grain" style={{ backgroundImage: "url(/noise.png)" }} aria-hidden />
      <div ref={ringRef} className="v2-cursor" aria-hidden />
    </div>
  );
};

export default AuthShell;

/** Shared field styles for the auth forms (underlined, editorial). */
export const authField =
  "w-full border-0 border-b border-ink/25 bg-transparent px-0 py-3 text-base text-ink outline-none transition-colors placeholder:text-ink/35 focus:border-tangerine";
export const authLabel = "block font-mono text-[10px] uppercase tracking-[0.22em] text-ink/50";
export const authError = "mt-2 font-mono text-[10px] uppercase tracking-[0.16em] text-tangerine";
